import {
  BAND_INTERVAL_MINUTES,
  INTRADAY_DEFAULTS,
  INTRADAY_RANGES,
  MARKET_IDS,
  POLICY_DEFAULTS,
  policyLimitKind,
} from "@atc/domain";
import type { ArgSpec, CommandDoc, FlagSpec } from "./spec";

/**
 * What every command is, declared once (`DESIGN-cli-command-spec`).
 *
 * Help, the flag parser, Tab and "did you mean" all read this, and
 * `commands.ts` pairs each entry with the handler that runs it — the two key
 * sets are the same by type, so a command cannot exist without a page or a
 * page without a command.
 *
 * **Every bound here is somebody else's number.** Intraday defaults and ranges
 * are `@atc/domain`'s, and so are the policy defaults and market ids, because
 * a help page that says `1–390` is only true while the API refuses 391.
 * `catalog.test.ts` runs every example through the real grammar.
 */

/** What `agent run` accepts — fixed words, because the server's list is fixed. */
export const AGENT_STRATEGIES = ["momentum", "portfolio"] as const;

/** The candidates `strategy` can be asked about. The API resolves versions. */
export const STRATEGY_CANDIDATES = [
  "cash",
  "spy-hold",
  "qqq-hold",
  "equal-weight",
  "momentum",
  "momentum-det",
  "trend-following",
  "mean-reversion",
] as const;

/** The server refuses anything faster; refusing in the terminal says so without a round trip. */
export const MAX_REPLAY_SPEED = 3600;

const D = INTRADAY_DEFAULTS;
const R = INTRADAY_RANGES;

/** `25,000`, for a round figure in a sentence. */
const whole = (decimal: string) =>
  (decimal.split(".")[0] ?? "0").replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const symbol: ArgSpec = { name: "symbol", kind: "symbol", about: "a ticker, e.g. NVDA" };

const spreadFlag: FlagSpec = {
  name: "--max-spread",
  value: "bps",
  kind: "int",
  about: "widest bid/ask spread a real-time decision accepts (an account priced by real-time SIP)",
  default: 25,
  min: 1,
  max: 10000,
  for: "on",
};

/**
 * What a schedule that only buys takes, shared by `dca` and `shares` (M8/I10):
 * its pace, its cap, the spread it accepts and its exits (M8/E4, M8/I9). One
 * declaration, so the two help pages and the two parsers cannot drift apart.
 */
const buyingFlags: readonly FlagSpec[] = [
  {
    name: "--below",
    value: "bps",
    kind: "int",
    about:
      "buy only when the price is this many bps under the --lookback-minute mean of the feed's 1-minute bars · off unless set",
    min: R.below_bps.min,
    max: R.below_bps.max,
    for: "on",
  },
  {
    name: "--lookback",
    value: "minutes",
    kind: "int",
    about: "minutes of the feed's 1-minute bars in the mean --below reads",
    default: D.lookback,
    min: R.lookback.min,
    max: R.lookback.max,
    for: "on",
  },
  {
    name: "--flat-weekend",
    value: "on|off",
    kind: "switch",
    about:
      "sell everything in the last ten minutes of the week's last session, and buy nothing more that day · off unless set",
    default: "off",
    choices: ["on", "off"],
    for: "on",
  },
  {
    name: "--cooldown",
    value: "minutes",
    kind: "int",
    about: "after any exit, buy nothing for this many minutes · off unless set",
    min: R.cooldown_minutes.min,
    max: R.cooldown_minutes.max,
    for: "on",
  },
  {
    name: "--interval",
    value: "minutes",
    kind: "int",
    about: "minutes between buys",
    default: D.interval_minutes,
    min: R.interval_minutes.min,
    max: R.interval_minutes.max,
    for: "on",
  },
  {
    name: "--max",
    value: "shares",
    kind: "shares",
    about: "the most shares it may hold",
    default: D.max_position,
    for: "on",
  },
  spreadFlag,
  {
    name: "--tp-pct",
    value: "percent",
    kind: "number",
    about: "take profit once the position is up this percent of what it cost · off unless set",
    for: "on",
  },
  {
    name: "--tp-usd",
    value: "dollars",
    kind: "money",
    about: "take profit once the position is up this many dollars · off unless set",
    for: "on",
  },
  {
    name: "--sl-pct",
    value: "percent",
    kind: "number",
    about:
      "stop the loss once the position is down this percent of what it cost (below 100) · off unless set",
    for: "on",
  },
  {
    name: "--sl-usd",
    value: "dollars",
    kind: "money",
    about: "stop the loss once the position is down this many dollars · off unless set",
    for: "on",
  },
  {
    name: "--on-exit",
    value: "action",
    kind: "word",
    about:
      "what a hit take profit or stop loss does: sell-stop sells everything and stops, sell-continue sells everything and keeps buying, stop stops and keeps the shares",
    default: "sell-stop",
    choices: ["sell-stop", "sell-continue", "stop"],
    for: "on",
  },
];

/** What a take profit and stop loss do, on both kinds that have them. */
const exitTraps = [
  "take profit and stop loss measure the account's whole position in the symbol — shares you bought by hand count too",
  "on an account mirrored to Alpaca, the exit rests at Alpaca, not here: a gtc OCO on the whole shares, and — because Alpaca refuses an OCO on a fraction — one day order on the fraction, the stop if --sl is set, otherwise the take profit, placed again each morning; whichever fills first ends the exit, the rest is cancelled and sold, and our account follows Alpaca's position every tick",
  "on any other account — or with --on-exit stop — exits are checked each tick on the price the tick decides on: with --interval 15 a stop loss is looked at every 15 minutes, and a refused tick looks at nothing",
  "Alpaca refuses a buy while a sell rests on the same symbol, so each buy first cancels the exit there and the exit rests again on the next tick that does not buy; while nothing rests, our engine checks the exit each tick — and a sell you type for the symbol is refused while an exit rests",
];

/**
 * Since M7/I11 a schedule mirrors because its ACCOUNT is connected — there is
 * no flag, and the trap says what the connection does to a strategy that sells.
 */
/**
 * Since M7/E44 a schedule decides on the price its ACCOUNT is priced by — there
 * is no `--price` — and the trap says which that is and how it changes.
 */
const decidesTrap =
  "it decides on the price its account is priced by: real-time SIP when your own Alpaca key's plan includes it, the 15-minute-delayed tape otherwise — `dca on`/`band on` say which";

const mirrorTrap =
  "on an account connected with `account mirror connect alpaca`, every fill is also placed on its Alpaca account (paper, or live) — " +
  "a sell needs the shares there, or it is refused `mirror_no_position`; `sync` first makes the two accounts equal";

const allowlistTrap =
  "a US schedule refuses to start without `policy set symbol_allowlist <symbol>`, and " +
  "its notional must fit both `max_notional_per_order` and " +
  "`max_intraday_notional_per_order`";

/**
 * The rule's own parameters, shared by every `rebalance` form that takes them.
 * The server holds the same limits (`parseMomentumRank`) and refuses by name.
 */
/** The forms of `rebalance` that take a rule's settings. */
const RULE_FORMS = ["backtest", "tune", "preview", "on"] as const;

const rebalanceRuleFlags: readonly FlagSpec[] = [
  {
    name: "--top",
    value: "n",
    kind: "int",
    about: "how many names it holds",
    default: 5,
    min: 1,
    max: 1000,
  },
  {
    name: "--lookback",
    value: "sessions",
    kind: "int",
    about: "sessions of daily history the score reads — 252 is a year",
    default: 252,
    min: 5,
    max: 756,
  },
  {
    name: "--score",
    value: "return|return-vol",
    kind: "word",
    about:
      "rank by the return over the lookback, or that return over the volatility of daily returns",
    default: "return-vol",
    choices: ["return", "return-vol"],
  },
  {
    name: "--weight",
    value: "equal|inverse-vol|score",
    kind: "word",
    about: "how the cash is split: equally, more to calmer names, or more to higher scores",
    default: "equal",
    choices: ["equal", "inverse-vol", "score"],
  },
  {
    name: "--every",
    value: "day|week|month",
    kind: "word",
    about: "rebalance every session, or on the first session of each week or month",
    default: "month",
    choices: ["day", "week", "month"],
  },
  {
    name: "--session",
    value: "n",
    kind: "int",
    about:
      "with --every week or month, rebalance on its Nth trading session instead of the first; its last when it has fewer",
    default: 1,
    min: 1,
    max: 23,
  },
  {
    name: "--at",
    value: "open|close",
    kind: "word",
    about:
      "trade 30 minutes after the open on yesterday's closes, or 1 minute before the close (15:59 ET) on today's price",
    default: "open",
    choices: ["open", "close"],
  },
  {
    name: "--sell-at",
    value: "HH:MM",
    kind: "word",
    about:
      "sell at this New York minute, 09:30–15:59, taking the ranking then, on yesterday's closes unless --at close · with --at close it buys at 15:59; alone, it buys at this minute too; never with --at open",
    for: ["preview", "on"],
    suggest: ["09:45", "12:00", "15:30"],
  },
  {
    name: "--buy-at",
    value: "HH:MM",
    kind: "word",
    about:
      "buy at this New York minute, 09:30–15:59, what the sell picked · with --at open it sells at 10:00; alone, it sells at this minute too; never with --at close",
    for: ["preview", "on"],
    suggest: ["10:30", "12:16", "15:59"],
  },
  {
    name: "--stop-pct",
    value: "pct",
    kind: "number",
    about: "between rebalances, sell a holding this far under its entry · off unless set",
    min: 1,
    max: 90,
  },
  {
    name: "--band",
    value: "pct",
    kind: "number",
    about: "trade a held name back to target only when this far off it",
    default: 20,
    min: 0,
    max: 100,
  },
  {
    name: "--invest-pct",
    value: "pct",
    kind: "number",
    about:
      "the share of the account kept invested at each rebalance, gains reinvested up to it; the rest stays cash",
    default: 100,
    min: 1,
    max: 100,
  },
  {
    name: "--skip-spike",
    value: "pct",
    kind: "number",
    about:
      "pass over a pick that rose more than this percent over its last 5 sessions, and take the next-ranked stock · off unless set",
    min: 1,
    max: 100,
  },
  {
    name: "--market",
    value: "spy|qqq|either|both:n",
    kind: "word",
    about:
      "step aside at a rebalance while SPY, QQQ, either or both close under their n-session average (20–252), e.g. spy:200 · off unless set",
  },
  {
    name: "--market-action",
    value: "sell|hold",
    kind: "word",
    about:
      "what a rebalance does while --market is under its average: sell everything and hold cash, or keep the holdings and buy nothing",
    default: "sell",
    choices: ["sell", "hold"],
  },
  {
    name: "--universe",
    value: "default|allowlist|SYM,SYM",
    kind: "word",
    about: "what it picks from: the 50 large caps, your symbol_allowlist, or your own list",
    default: "default",
  },
  {
    name: "--exclude",
    value: "SYM,SYM",
    kind: "word",
    about: "leave these names out of the universe, whichever it is · none unless set",
  },
  {
    name: "--capital",
    value: "dollars",
    kind: "money",
    about: "the most it deploys; the rest stays cash · all of it unless set",
  },
  {
    name: "--tp-pct",
    value: "pct",
    kind: "number",
    about:
      "the ladder: between rebalances, each minute, sell all but 0.1% of a position this far over its entry · off unless set",
    min: 0.1,
    max: 100,
  },
  {
    name: "--tp-usd",
    value: "dollars",
    kind: "number",
    about: "the ladder's take profit as dollars on the whole position, instead of --tp-pct",
    min: 1,
    max: 1_000_000,
  },
  {
    name: "--rebuy-under",
    value: "pct",
    kind: "number",
    about:
      "the ladder rebuilds a sold position with its proceeds at the take-profit price less this · 0 is as soon as it is back under",
    default: 2,
    min: 0,
    max: 50,
  },
  {
    name: "--rebuy-above",
    value: "pct|off",
    kind: "word",
    about: "and rebuilds it when the price runs this far above its take-profit price, or never",
    default: 3,
  },
  {
    name: "--ladder-scale",
    value: "off|vol:N:P[:LO:HI][:LEGS]",
    kind: "word",
    about:
      "multiplies each position's ladder by its k: (its N-session volatility ÷ the universe's median), kept within LO–HI (0.4–2.5), to the power P; LEGS picks tp, under and above (all) · needs --tp-pct · off unless set",
    suggest: ["off", "vol:40:0.75"],
  },
  {
    name: "--when",
    value: "off|pick-ret:N>X|dispersion:N>X",
    kind: "word",
    about:
      "a condition read at each rebalance: the pick's N-session return (pick-ret) or how widely the universe's N-session returns spread (dispersion), in percent, > or < X; X is a percent, or pQ:W, the Q-th percentile of the same measure over the last W weeks. While it holds, the week runs --then's flags · off unless set",
    suggest: ["off", "dispersion:20>p75:26", "pick-ret:20>p50:52"],
  },
  {
    name: "--then",
    value: "key=value,…",
    kind: "word",
    about:
      "the flags a week runs on while --when holds, in place of its own: market (or off), market-action, tp-pct, rebuy-under, rebuy-above, ladder-scale; the pick never changes · needs --when, and a ladder of its own to change one",
    suggest: ["market=spy:200,tp-pct=1,rebuy-under=2,rebuy-above=4,ladder-scale=off"],
  },
];

export const CATALOG = {
  accounts: {
    group: "accounts & markets",
    summary: "list your named paper accounts and the feed pricing each",
    forms: [
      {
        syntax: "accounts",
        does: "every active account and its feed, with the selected account marked",
      },
      {
        syntax: "accounts closed",
        does: "closed accounts, with the id `account delete` takes when two share a name",
      },
    ],
    examples: [
      { line: "accounts", says: "shows each account beside the feed that prices it" },
      { line: "accounts closed", says: "lists what `account close` hid, so it can be deleted" },
    ],
    seeAlso: ["account", "feed"],
  },

  account: {
    group: "accounts & markets",
    summary: "inspect or manage your named paper accounts",
    forms: [
      { syntax: "account", does: "the selected account" },
      { syntax: "account <name>", does: "select an existing account" },
      {
        syntax: "account create <name>",
        does: "open and select a funded paper account, priced by the feed it opens on — $100,000 unless --cash says otherwise",
      },
      {
        syntax: "account mirror",
        does: "the Alpaca account — paper or LIVE — the selected account trades through",
      },
      {
        syntax: "account mirror connect <broker>",
        does: "give the selected account a broker paper account of its own: every order is placed there too, exits rest there, and `sync` reads it — the key is asked for, never echoed",
      },
      {
        syntax: "account mirror connect <broker> live",
        does: "make your LIVE Alpaca account the selected account's one venue instead — real money from the moment it connects; operator only",
      },
      {
        syntax: "account mirror disconnect",
        does: "disconnect the selected account's Alpaca account, paper or live",
      },
      { syntax: "account rename <old> <new>", does: "rename an account" },
      { syntax: "account close <name>", does: "close an empty account" },
      {
        syntax: "account delete <name>",
        does: "erase an account, open or closed, and its whole history — after typing its name",
      },
    ],
    args: [
      { name: "name", kind: "word", about: "letters, digits, - and _, up to 64" },
      { name: "old", kind: "word", about: "existing account name" },
      { name: "new", kind: "word", about: "new account name" },
      {
        name: "broker",
        kind: "word",
        about: "a broker, e.g. alpaca",
        choices: ["alpaca"],
      },
    ],
    flags: [
      {
        name: "--feed",
        value: "provider",
        kind: "feed",
        about: "the feed that prices it — a name `feed` lists",
        default: "synthetic",
        for: "create",
      },
      {
        name: "--cash",
        value: "dollars",
        kind: "money",
        about: "the cash it opens with; an account that already exists keeps its own",
        default: 100000,
        min: 1,
        max: 100000000,
        for: "create",
      },
    ],
    examples: [
      { line: "account m7-sip", says: "switches to the account named m7-sip" },
      {
        line: "account create lab",
        says: "opens a paper account on the simulator, which trades at any hour",
      },
      {
        line: "account create sell-test --feed alpaca --cash 4000",
        says: "opens a $4,000 paper account priced by your Alpaca feed, and selects it",
      },
      {
        line: "account create m7-sip --feed alpaca",
        says: "opens one priced by your Alpaca key, if you are entitled to it",
      },
      {
        line: "account mirror",
        says: "shows which Alpaca account, paper or live, the selected account trades through",
      },
      {
        line: "account mirror connect alpaca",
        says: "asks for that account's own paper key id and secret, and checks them before storing",
      },
      {
        line: "account mirror connect alpaca live",
        says: "asks for your LIVE key id and secret, checks Alpaca's live API takes them, and from then on places every order there — follow with `sync preview` and `sync`",
      },
      {
        line: "account delete lab",
        says: "shows what erasing lab destroys, then asks you to type lab — and, with orders open, to discard them",
      },
    ],
    traps: [
      "two strategies on one account share its cash and holdings — give each its own named account to compare them",
      "`account delete` cannot be undone: orders, fills, the ledger and execution probes go with the account — and Alpaca paper keeps its own orders and positions",
      "each account has its own feed — `feed <provider>` switches only the selected one",
      "opening a name that already exists selects it as it is; its feed does not change",
      "an account has ONE Alpaca account, paper or live: connecting either kind replaces the other, and switching is connecting the other key, then `sync`",
      "a mirror key is the account's own: two accounts need two Alpaca accounts, and one broker account can belong to only one trading account",
      "the key `feed connect` takes prices every account and never receives orders — orders go only to `account mirror connect`",
      "a live connection spends REAL money with no off switch: every order is placed at the same quantity with no cap — `account mirror disconnect` is how it stops",
      "a live key pasted into `account mirror connect alpaca` (without `live`) is refused and nothing is stored",
      "a live order fills at its own price and time: `sync` makes this account match the live one again",
      "`account live` is retired — it only points here; a bare `account live` still selects an account named live",
    ],
    seeAlso: ["accounts", "feed", "positions", "dca", "band", "sync"],
  },

  sync: {
    group: "accounts & markets",
    summary: "reset the selected account to its connected Alpaca account — paper, or live",
    forms: [
      {
        syntax: "sync",
        does: "make cash, positions and open orders equal the Alpaca account this account is connected to — the live one on a live connection",
      },
      { syntax: "sync preview", does: "what a sync would change, changing nothing" },
      {
        syntax: "sync every <interval>",
        does: "sync on a schedule, every HH:MM — or `off` to stop",
      },
      { syntax: "sync log", does: "recent syncs of this account, refusals included" },
      { syntax: "sync show <id>", does: "one sync: what Alpaca said and everything it appended" },
    ],
    args: [
      {
        name: "interval",
        kind: "word",
        about: "HH:MM from 00:15 to 24:00, or off",
        choices: ["00:15", "00:30", "01:00", "04:00", "24:00", "off"],
      },
      { name: "id", kind: "id", about: "the first characters of an id from `sync log`" },
    ],
    examples: [
      { line: "sync preview", says: "shows what a sync would close, open, move and re-create" },
      {
        line: "sync",
        says: "resets the account to its connected Alpaca account, and says what changed",
      },
      { line: "sync every 01:00", says: "syncs the account every hour, on its own" },
      { line: "sync every off", says: "stops the scheduled sync" },
      { line: "sync log", says: "lists the recent syncs, with each refusal's reason" },
    ],
    traps: [
      "Alpaca is the source of truth at the moment of a sync — positions, cash and open orders here are replaced by its",
      "history is kept: a sync appends closing and opening fills and one cash transfer, and deletes nothing",
      "our open orders are cancelled; Alpaca's are re-created here, except what this engine cannot hold (brackets, notional, trailing stops)",
      "the equity a sync moves is an external flow — evals and perf never score it as a gain or a loss",
      "needs `account mirror connect alpaca` (or `… alpaca live`), and a feed that prices US equities; a key that no longer works changes nothing",
      "a live margin account can show negative cash: positions and orders still sync, cash here stops at 0, and the shortfall is in the plan",
      "a sync only reads Alpaca — it never places, cancels or changes anything there",
    ],
    seeAlso: ["account", "positions", "gap"],
  },

  delegate: {
    group: "terminal",
    summary: "grant or revoke bounded read-only operator inspection",
    forms: [
      {
        syntax: "delegate grant all [--expires <duration>]",
        does: "grant access to all current and future accounts",
      },
      {
        syntax: "delegate grant <account> [<account>...] [--expires <duration>]",
        does: "grant access to named accounts",
      },
      { syntax: "delegate list", does: "list grants without their token material" },
      { syntax: "delegate revoke <id>", does: "revoke one grant" },
      { syntax: "delegate revoke all", does: "revoke every grant you own" },
    ],
    args: [
      { name: "account", kind: "word", about: "a named account" },
      { name: "duration", kind: "word", about: "HH:MM, up to 24:00" },
      { name: "id", kind: "id", about: "a delegation id" },
    ],
    flags: [
      {
        name: "--expires",
        value: "HH:MM",
        kind: "word",
        about: "duration up to 24:00",
        default: "24:00",
        for: "grant",
      },
    ],
    examples: [
      { line: "delegate grant all --expires 02:30", says: "prints a short-lived token once" },
    ],
    seeAlso: ["accounts"],
  },

  agent: {
    group: "agents",
    summary: "run an agent, answer it, and schedule it",
    forms: [
      { syntax: "agent", does: "your recent runs" },
      { syntax: "agent run <strategy>", does: "run one now and wait for what it decides" },
      { syntax: "agent status [<run-id>]", does: "one run in detail, or the latest" },
      { syntax: "agent approve [<run-id>]", does: "let a run that is waiting place its orders" },
      {
        syntax: "agent reject [<run-id> [<why>...]]",
        does: "refuse a waiting run, with a reason it records",
      },
      { syntax: "agent perf", does: "what the agent's paper account has made or lost" },
      { syntax: "agent evals [<days>]", does: "the scored record over the last days" },
      { syntax: "agent backtest [<strategy>]", does: "the strategy replayed over history" },
      { syntax: "agent config", does: "the scheduled momentum agent" },
      {
        syntax: "agent config momentum daily|weekly|enable|disable",
        does: "set its cadence, or turn it on or off",
      },
    ],
    args: [
      {
        name: "strategy",
        kind: "word",
        about: AGENT_STRATEGIES.join(" or "),
        choices: AGENT_STRATEGIES,
      },
      {
        name: "run-id",
        kind: "id",
        about: "a run id from `agent` — the latest waiting run if left out",
      },
      { name: "why", kind: "text", about: "a sentence, recorded with the refusal" },
      { name: "days", kind: "int", about: "how far back" },
    ],
    examples: [
      {
        line: "agent run momentum",
        says: "runs the momentum agent now and prints what it decided",
      },
      { line: "agent approve", says: "approves the latest run waiting for an answer" },
      { line: "agent config momentum weekly", says: "schedules the momentum agent once a week" },
      { line: "agent evals 30", says: "its scored record over the last 30 days" },
    ],
    traps: [
      "an agent's orders pass the same `policy` limits as yours — `policy set agents_may_trade off` stops them",
    ],
    seeAlso: ["spend", "insights", "strategy", "policy"],
  },

  band: {
    group: "strategies",
    summary: "buy below and sell above a rolling mean, one tranche at a time",
    forms: [
      {
        syntax: "band on <symbol>",
        does: "start or replace it: each minute, buy a tranche when the price is --band below the --lookback mean, sell one when it is as far above",
      },
      { syntax: "band off <symbol>", does: "stop it; what it holds stays in the account" },
      { syntax: "band log <symbol>", does: "the schedule, then every tick it took, newest first" },
    ],
    args: [symbol],
    flags: [
      {
        name: "--band",
        value: "bps",
        kind: "int",
        about: "distance from the mean, in basis points",
        default: D.band_bps,
        min: R.band_bps.min,
        max: R.band_bps.max,
        for: "on",
      },
      {
        name: "--tranche",
        value: "amount",
        kind: "money",
        about: "dollars per trade",
        default: D.tranche,
        for: "on",
      },
      {
        name: "--core",
        value: "shares",
        kind: "shares",
        about: "shares it never sells below",
        default: D.core,
        for: "on",
      },
      {
        name: "--max",
        value: "shares",
        kind: "shares",
        about: "the most shares it may hold",
        default: D.max_position,
        for: "on",
      },
      {
        name: "--lookback",
        value: "minutes",
        kind: "int",
        about: "minutes of the feed's 1-minute bars in the rolling mean",
        default: D.lookback,
        min: R.lookback.min,
        max: R.lookback.max,
        for: "on",
      },
      spreadFlag,
    ],
    examples: [
      {
        line: "band on NVDA --max 12",
        says: `trades ${D.tranche} tranches at ±${D.band_bps} bps around a ${D.lookback}-minute mean, holding at most 12 shares`,
      },
      {
        line: "band on NVDA --tranche 50 --band 25 --lookback 60 --core 2 --max 10",
        says: "trades 50 at ±25 bps around an hour's mean, never below 2 shares or above 10",
      },
      { line: "band log NVDA", says: "whether it is running, then what each minute did" },
    ],
    traps: [
      "its mean is read from the feed's 1-minute bars on every tick, so it can trade on its first — if that history cannot be read it falls back to what it has seen itself, warms up for --lookback minutes, and `band log` says why",
      `it ticks every ${BAND_INTERVAL_MINUTES} minute and takes no notional — its size is --tranche`,
      `without --max it may hold ${D.max_position} shares`,
      allowlistTrap,
      decidesTrap,
      mirrorTrap,
    ],
    seeAlso: ["dca", "gap", "schedules", "policy", "account"],
  },

  buy: {
    group: "trading",
    summary: "buy at the market, or rest a limit or stop order",
    forms: [
      {
        syntax: "buy <qty> <symbol> [limit <price>] [stop <price>] [<tif>]",
        does: "a market order fills now at the quote; a limit or stop rests until it can fill",
      },
    ],
    args: [
      { name: "qty", kind: "shares", about: "shares — fractions allowed" },
      symbol,
      { name: "price", kind: "money", about: "the limit or stop price" },
      { name: "tif", kind: "tif", about: "how long it may rest: day, gtc, ioc or fok" },
    ],
    examples: [
      { line: "buy 10 AAPL", says: "buys 10 shares at the market" },
      {
        line: "buy 5 NVDA limit 180 gtc",
        says: "rests until NVDA can be bought at 180 or less, until cancelled",
      },
    ],
    traps: [
      "a market order cannot be gtc — it fills now or not at all",
      "every order passes your `policy` limits first",
    ],
    seeAlso: ["sell", "orders", "cancel", "policy"],
  },

  cancel: {
    group: "trading",
    summary: "cancel a resting order",
    forms: [{ syntax: "cancel <id>", does: "cancel the open order whose id starts with <id>" }],
    args: [{ name: "id", kind: "id", about: "the first characters of an id from `orders open`" }],
    examples: [
      { line: "cancel 3f2a9c1e", says: "cancels the one open order whose id starts 3f2a9c1e" },
    ],
    seeAlso: ["orders"],
  },

  clear: {
    group: "terminal",
    summary: "clear the screen",
    forms: [{ syntax: "clear", does: "empty the scrollback" }],
    examples: [{ line: "clear", says: "a blank screen", local: true }],
  },

  curve: {
    group: "accounts & markets",
    summary: "the account's recorded valuation points — its equity curve",
    forms: [
      {
        syntax: "curve",
        does: "every point recorded in the last --days days, oldest first: equity, cash, holdings and what buy-and-hold would be worth",
      },
    ],
    flags: [
      {
        name: "--days",
        value: "days",
        kind: "int",
        about: "how far back",
        default: 30,
        min: 1,
        max: 366,
      },
    ],
    examples: [
      { line: "curve", says: "the last 30 days of this account's curve" },
      { line: "curve --days 1", says: "today's half-hourly points" },
    ],
    traps: [
      "points are recorded half-hourly while a schedule runs and at the edges of agent runs — the line between two points is not a price anyone saw",
      "two strategies sharing an account share one curve; compare them on separate accounts, at matched exposure",
    ],
    seeAlso: ["dca", "band", "gap", "account"],
  },

  record: {
    group: "prices",
    summary:
      "record your live quote tape once a minute, in the app — nothing left running on a laptop",
    forms: [
      {
        syntax: "record",
        does: "whether your recorder runs, what it records, and what its last minute did",
      },
      {
        syntax: "record on <symbols>",
        does: "record these symbols once a minute through the US session and the sixteen minutes after it, every trading day until you stop it",
      },
      { syntax: "record off", does: "stop recording — what was recorded stays" },
      {
        syntax: "record export",
        does: "save one day of your tape as <date>.json, in the format to commit under intraday/",
      },
    ],
    args: [
      {
        name: "symbols",
        kind: "word",
        about: "comma-separated tickers, up to 20, e.g. SPY,QQQ,NVDA,AAPL",
      },
    ],
    flags: [
      {
        name: "--date",
        value: "date",
        kind: "date",
        about: "the UTC day of the prints · default today",
        for: "export",
      },
    ],
    examples: [
      { line: "record", says: "is it recording, and did its last minute record anything" },
      {
        line: "record on SPY,QQQ,NVDA,AAPL",
        says: "starts recording those four; running it again replaces the list",
      },
      { line: "record off", says: "stops it" },
      {
        line: "record export --date 2026-09-11",
        says: "saves 2026-09-11.json, the day's prints oldest first",
      },
    ],
    traps: [
      "only live prices are recorded — a guest, or a trader on the simulator, is refused rather than recording nothing all day",
      "the prices are your delayed feed's, so a print's time is when it happened, not when it was recorded; the tail after the close is part of that day",
      "a minute with no new print records nothing, and that is not a failure",
      "the tape is yours alone — export it and commit it; the app never publishes it",
    ],
    seeAlso: ["feed", "watch", "gap"],
  },

  dca: {
    group: "strategies",
    summary: "buy a fixed amount of one symbol on a schedule",
    forms: [
      {
        syntax: "dca on <symbol> [<notional>]",
        does: "start or replace it: buy <notional> of <symbol> every --interval minutes while the market is open",
      },
      { syntax: "dca off <symbol>", does: "stop it; what it bought stays in the account" },
      { syntax: "dca log <symbol>", does: "the schedule, then every tick it took, newest first" },
    ],
    args: [
      symbol,
      { name: "notional", kind: "money", about: `dollars per buy · default ${D.notional}` },
    ],
    flags: [...buyingFlags],
    examples: [
      {
        line: "dca on NVDA 100 --interval 15 --max 12",
        says: "buys 100 of NVDA every 15 minutes, never holding more than 12 shares",
      },
      {
        line: "dca on NVDA 100 --tp-pct 5 --sl-usd 200",
        says: "buys every minute until NVDA is up 5% or down $200, whichever comes first, then sells it all and stops",
      },
      { line: "dca log NVDA", says: "whether it is running, then what each tick did" },
      { line: "dca off NVDA", says: "stops buying; the shares stay" },
    ],
    traps: [
      `without --max it may hold ${D.max_position} shares — for a $200 stock that is $20,000`,
      allowlistTrap,
      "`dca on` again replaces the schedule; it does not add a second one",
      ...exitTraps,
      decidesTrap,
      mirrorTrap,
    ],
    seeAlso: ["band", "gap", "schedules", "policy", "account"],
  },

  shares: {
    group: "strategies",
    summary: "buy a fixed number of shares of one symbol on a schedule",
    forms: [
      {
        syntax: "shares on <symbol> [<qty>]",
        does: "start or replace it: buy <qty> shares of <symbol> every --interval minutes while the market is open",
      },
      { syntax: "shares off <symbol>", does: "stop it; what it bought stays in the account" },
      {
        syntax: "shares log <symbol>",
        does: "the schedule, then every tick it took, newest first",
      },
    ],
    args: [symbol, { name: "qty", kind: "shares", about: `shares per buy · default ${D.qty}` }],
    flags: [...buyingFlags],
    examples: [
      {
        line: "shares on MSFT 0.1 --interval 2 --max 8 --tp-usd 100 --on-exit sell-continue",
        says: "buys 0.1 MSFT every 2 minutes up to 8 shares; each time the position is up $100 it sells it all and starts again",
      },
      {
        line: "shares log MSFT",
        says: "whether it is running, where its exit rests, then what each tick did",
      },
      { line: "shares off MSFT", says: "stops buying; the shares stay" },
    ],
    traps: [
      `--max is the real limit: without it it may hold ${D.max_position} shares — for a $500 stock that is $50,000`,
      "each buy costs qty × the price, so it moves with the market — a buy the price has grown past your per-order limit is refused at that tick, and `shares log` says which limit",
      allowlistTrap,
      "`shares on` again replaces the schedule; it does not add a second one",
      "below one share only the fraction's day order rests at Alpaca — the stop, or the take profit when there is no stop — so the first buys are covered on one side only",
      ...exitTraps,
      decidesTrap,
      mirrorTrap,
    ],
    seeAlso: ["dca", "band", "gap", "schedules", "policy"],
  },

  gap: {
    group: "strategies",
    summary: "your orders against what the same orders did on your connected Alpaca account",
    forms: [
      {
        syntax: "gap",
        does: "every order on the account this session — typed, an agent's, a schedule's — beside its Alpaca mirror",
      },
      {
        syntax: "gap dca|band|shares <symbol>",
        does: "one session's orders: our fill, Alpaca's fill, the execution gap in bps and the quantity gap, both marked at one price",
      },
    ],
    args: [symbol],
    flags: [
      {
        name: "--date",
        value: "date",
        kind: "date",
        about: "the New York trading date · default today",
      },
    ],
    examples: [
      { line: "gap", says: "today's orders on the selected account, ours against Alpaca's" },
      { line: "gap dca NVDA", says: "today's dca orders on NVDA, planned against real" },
      {
        line: "gap band NVDA --date 2026-09-11",
        says: "that session's band orders, and the gap against each tick's SIP quote",
      },
    ],
    traps: [
      "the execution gap and the quantity gap are two numbers, never one — a partial fill at Alpaca is a quantity gap, not a price",
      "only orders placed while the account was connected (`account mirror connect alpaca`) have a real side",
      "the SIP column is there only where your key could fetch real-time SIP in that tick",
    ],
    seeAlso: ["dca", "band", "schedules"],
  },

  feed: {
    group: "prices",
    summary: "choose which feed prices the selected account",
    forms: [
      {
        syntax: "feed",
        does: "what may price the selected account, what is pricing it, and why",
      },
      {
        syntax: "feed <provider>",
        does: "switch the selected account to it — its resting orders are canceled, its positions kept",
      },
      {
        syntax: "feed connect <provider>",
        does: "connect a broker's paper key — asked for, never echoed",
      },
      { syntax: "feed disconnect [<provider>]", does: "disconnect a key you connected" },
    ],
    args: [{ name: "provider", kind: "feed", about: "a name `feed` lists, e.g. alpaca" }],
    examples: [
      { line: "feed", says: "lists your feeds and which one prices the selected account" },
      {
        line: "feed alpaca",
        says: "prices the selected account from your Alpaca key, if you are entitled to it",
      },
      {
        line: "feed connect alpaca",
        says: "asks for a paper key id and secret, and checks them before storing",
        local: true,
      },
    ],
    traps: [
      "only a PAPER key is accepted — a live key is refused before anything is stored",
      "a switch cancels the account's resting orders, and stops schedules and agents set up for a market the new feed does not price",
      "a real feed may not carry every symbol — a position it cannot price shows its cost, and cannot be traded until a feed carries it",
      "the key you connect here prices every account you own and never receives orders — each account mirrors into its own, `account mirror connect alpaca`",
    ],
    seeAlso: ["account", "quote", "watch"],
  },

  help: {
    group: "getting started",
    summary: "every command, or one explained",
    forms: [
      {
        syntax: "help",
        does: "the index, grouped — with your first-run checklist while any step is left",
      },
      {
        syntax: "help <command>",
        does: "one command: its forms, arguments, flags, defaults, examples and traps",
      },
    ],
    args: [
      {
        name: "command",
        kind: "command",
        about: "any command; `<command> --help` shows the same page",
      },
    ],
    examples: [
      { line: "help dca", says: "the page for dca", local: true },
      { line: "band --help", says: "the page for band", local: true },
    ],
  },

  insights: {
    group: "agents",
    summary: "what an agent notices about your portfolio — it places no orders",
    forms: [{ syntax: "insights", does: "run the portfolio agent and print what it wrote" }],
    examples: [{ line: "insights", says: "observations about what you hold" }],
    seeAlso: ["agent"],
  },

  login: {
    group: "getting started",
    summary: "sign in with a code sent to your email",
    forms: [
      { syntax: "login", does: "asks for your email, sends a code, then asks for the code" },
      { syntax: "login <email> [<code>]", does: "the same, typed on one line" },
    ],
    args: [
      { name: "email", kind: "email", about: "the address the code goes to" },
      { name: "code", kind: "code", about: "the code from the email" },
    ],
    examples: [{ line: "login you@example.com", says: "sends a code and asks for it" }],
    traps: ["the code lasts 10 minutes, and we never send a sign-in link"],
    seeAlso: ["whoami", "logout"],
  },

  logout: {
    group: "getting started",
    summary: "sign out, and close the live connection",
    forms: [{ syntax: "logout", does: "end the session now" }],
    examples: [{ line: "logout", says: "signed out" }],
  },

  market: {
    group: "accounts & markets",
    summary: "one venue in detail",
    forms: [{ syntax: "market <market>", does: "its hours, its session now and its next open" }],
    args: [{ name: "market", kind: "market", about: MARKET_IDS.join(" or ") }],
    examples: [{ line: "market US", says: "the New York session, and whether it is open" }],
    seeAlso: ["markets"],
  },

  markets: {
    group: "accounts & markets",
    summary: "which venues exist, and whether each is open",
    forms: [{ syntax: "markets", does: "every venue, open or closed" }],
    examples: [{ line: "markets", says: "US and SIM, and their sessions" }],
    seeAlso: ["market", "account"],
  },

  orders: {
    group: "trading",
    summary: "your orders, newest first",
    forms: [
      {
        syntax: "orders [open] [<count>]",
        does: "the latest orders; `open` shows only those still resting",
      },
    ],
    args: [{ name: "count", kind: "int", about: "how many · default 20 · at most 200" }],
    examples: [
      { line: "orders open", says: "what is still resting, and could still fill" },
      { line: "orders 50", says: "the last 50 orders" },
    ],
    seeAlso: ["cancel", "positions"],
  },

  policy: {
    group: "risk & policy",
    summary: "the limits every order is checked against",
    forms: [
      { syntax: "policy", does: "your limits, and any account not enforcing them" },
      { syntax: "policy set <limit> <value>", does: "change one limit; the others stay" },
      {
        syntax: "policy halt [<reason>...]",
        does: "operators only: stop all trading, platform-wide",
      },
      { syntax: "policy resume", does: "operators only: lift a halt" },
    ],
    args: [
      { name: "limit", kind: "limit", about: "one of the eight limits `policy` lists" },
      {
        name: "value",
        kind: "text",
        about:
          "on/off, an amount, a whole number, or symbols separated by commas — `none` empties a list",
        choices: (typed) => {
          const kind = policyLimitKind(typed[1]?.toLowerCase() ?? "");
          return kind === "switch" ? ["on", "off"] : kind === "symbols" ? ["none"] : undefined;
        },
      },
      { name: "reason", kind: "text", about: "why, recorded with the halt" },
    ],
    examples: [
      { line: "policy", says: "your eight limits" },
      { line: "policy set symbol_allowlist NVDA", says: "only NVDA may be traded" },
      {
        line: "policy set max_notional_per_order 105",
        says: "no single order may be worth more than 105.00",
      },
      {
        line: "policy set max_intraday_notional_per_order 100",
        says: "a DCA notional or band tranche may be worth up to 100.00",
      },
      {
        line: "policy set kill_switch on",
        says: "refuses every order on your accounts until it is off",
      },
    ],
    traps: [
      `with nothing set: ${whole(POLICY_DEFAULTS.max_notional_per_order)} per order, ` +
        `${whole(POLICY_DEFAULTS.max_position_notional)} per position, ${POLICY_DEFAULTS.max_orders_per_day} orders a day`,
      "an empty allowlist permits every symbol — and a US dca or band schedule refuses to start without one",
    ],
    seeAlso: ["dca", "band", "agent"],
  },

  positions: {
    group: "trading",
    summary: "what you hold, valued at the current mark",
    forms: [{ syntax: "positions", does: "each holding, its value and P&L, then cash and equity" }],
    examples: [{ line: "positions", says: "what you hold and what it is worth" }],
    seeAlso: ["orders", "account"],
  },

  privacy: {
    group: "getting started",
    summary: "the privacy policy",
    forms: [{ syntax: "privacy", does: "where the privacy policy is, on the web" }],
    examples: [{ line: "privacy", says: "the address of the privacy policy", local: true }],
    seeAlso: ["terms"],
  },

  quote: {
    group: "prices",
    summary: "the current price of one or more symbols",
    forms: [{ syntax: "quote <symbol>...", does: "last, bid and ask for each" }],
    args: [symbol],
    examples: [{ line: "quote NVDA AAPL", says: "two prices, once — `watch` keeps them moving" }],
    seeAlso: ["watch", "feed"],
  },

  replay: {
    group: "prices",
    summary: "play a recorded market day into the ticker",
    forms: [
      { syntax: "replay", does: "list the recorded days", noFlags: true },
      { syntax: "replay <date> [<symbol>...]", does: "play that day into the ticker" },
      { syntax: "replay stop", does: "stop playing", noFlags: true },
    ],
    args: [
      { name: "date", kind: "date", about: "YYYY-MM-DD, one of the days `replay` lists" },
      symbol,
    ],
    flags: [
      {
        name: "--speed",
        value: "n",
        kind: "number",
        about: `times faster than real time, up to ${MAX_REPLAY_SPEED}`,
        default: 60,
      },
    ],
    examples: [
      { line: "replay", says: "the days that were recorded" },
      {
        line: "replay 2026-08-14 --speed 120 NVDA",
        says: "plays NVDA's 14 August 2026 into the ticker at 120×",
      },
      { line: "replay stop", says: "back to live prices", local: true },
    ],
    traps: ["a replayed spread is modelled — a recorded bar has no bid or ask"],
    seeAlso: ["watch"],
  },

  schedules: {
    group: "strategies",
    summary: "every dca and band schedule on any of your accounts, and whether it is running",
    forms: [
      {
        syntax: "schedules",
        does: "each schedule's status, account and last tick — whatever may be trading is listed first",
      },
    ],
    examples: [{ line: "schedules", says: "nothing is running — or exactly which schedules are" }],
    traps: [
      "a schedule keeps trading straight through a deploy — this is how to know that none is",
      "`unknown` means its schedule did not answer, not that it stopped",
    ],
    seeAlso: ["dca", "band", "account"],
  },

  status: {
    group: "terminal",
    summary: "is anything wrong — your feed, your schedules, and for an operator the whole system",
    forms: [
      {
        syntax: "status",
        does: "your slice: the feed pricing this account, your schedules, and anything waiting on you",
      },
      {
        syntax: "status system",
        does: "operators only: every rule the sweep judged, open incidents and the last 15 minutes",
      },
      { syntax: "status incidents", does: "operators only: what you were told, and when" },
    ],
    examples: [
      { line: "status", says: "whether your prices are fresh and your schedules are ticking" },
      { line: "status system", says: "each rule's verdict, open incidents, errors and latency" },
      {
        line: "status incidents",
        says: "the incident log, newest first, and whether each was emailed",
      },
    ],
    traps: [
      "`status` is what the sweep saw within the last minute, not a fresh probe",
      "a schedule is never overdue while its market is closed",
      "`status system` needs an operator account — everyone else sees only their own slice",
    ],
    seeAlso: ["schedules", "feed", "agent"],
  },

  sell: {
    group: "trading",
    summary: "sell at the market, or rest a limit or stop order",
    forms: [
      {
        syntax: "sell <qty> <symbol> [limit <price>] [stop <price>] [<tif>]",
        does: "a market order fills now at the quote; a limit or stop rests until it can fill",
      },
    ],
    args: [
      { name: "qty", kind: "shares", about: "shares — fractions allowed" },
      symbol,
      { name: "price", kind: "money", about: "the limit or stop price" },
      { name: "tif", kind: "tif", about: "how long it may rest: day, gtc, ioc or fok" },
    ],
    examples: [
      { line: "sell 5 NVDA", says: "sells 5 shares at the market" },
      {
        line: "sell 5 NVDA stop 170",
        says: "rests until NVDA trades at 170, then sells at the market",
      },
    ],
    traps: ["a market order cannot be gtc — it fills now or not at all"],
    seeAlso: ["buy", "orders", "positions"],
  },

  setup: {
    group: "terminal",
    summary: "change how the terminal looks",
    forms: [
      {
        syntax: "setup [<setup>]",
        does: "list the looks, or switch to one — the choice survives a reload",
      },
    ],
    args: [{ name: "setup", kind: "setup", about: "a look `setup` lists" }],
    examples: [{ line: "setup", says: "the looks, and which one you are wearing", local: true }],
    seeAlso: ["theme"],
  },

  spend: {
    group: "agents",
    summary: "what your agents cost today, and what is left",
    forms: [{ syntax: "spend", does: "today's model spend against its ceiling" }],
    examples: [{ line: "spend", says: "spent, remaining and the ceiling" }],
    seeAlso: ["agent"],
  },

  rebalance: {
    group: "strategies",
    summary: "hold the top N of a universe by momentum, rebalanced on a cadence",
    forms: [
      {
        syntax: "rebalance",
        does: "this account's rebalance: its settings, when it next acts, its last decision",
        noFlags: true,
      },
      {
        syntax: "rebalance status",
        does: "the same, for one setting — --name picks which of the account's settings",
      },
      {
        syntax: "rebalance backtest",
        does: "the rule over daily history since 2020, up to yesterday, beside SPY and equal weight",
      },
      {
        syntax: "rebalance tune",
        does: "try a grid of settings on one period and show how the winner did on the next — --top, --lookback, --score and --weight each take a list like 3,5,10; Escape stops following, the tune keeps running",
      },
      {
        syntax: "rebalance tune status [<id>]",
        does: "follow a running tune again, or show a finished one's report — the latest without an id",
        noFlags: true,
      },
      {
        syntax: "rebalance preview",
        does: "what this account's rebalance would buy and sell if it ran right now, on its current settings — nothing is placed. Give any setting and it previews those settings instead",
      },
      {
        syntax: "rebalance on",
        does: "start or replace it on this account; it trades on its cadence from then on",
      },
      {
        syntax: "rebalance now",
        does: "rebalance once, now, as its next alarm would — it trades",
      },
      {
        syntax: "rebalance off [sell] [forget]",
        does: "stop it; `sell` also sells its book, `forget` then removes a named setting so its name and share are free",
      },
      {
        syntax: "rebalance log [<n>]",
        does: "its last n decisions, newest first, with every order's outcome",
      },
      { syntax: "rebalance list", does: "every account's rebalance", noFlags: true },
      {
        syntax: "rebalance compare <account> [<account>]",
        does: "two rebalance accounts over the same days, beside SPY — this one and another, or any two; `<account>:<setting>` picks one setting of a shared account",
        noFlags: true,
      },
    ],
    args: [
      { name: "n", kind: "int", about: "how many decisions · default 5" },
      { name: "account", kind: "word", about: "an account's name, as `accounts` lists it" },
      { name: "id", kind: "id", about: "a tune's id, as `rebalance tune` prints it" },
    ],
    flags: [
      // The rule's settings belong to the forms that take a rule, not to `now`, `off` or `log`.
      ...rebalanceRuleFlags.map((f) => ({ ...f, for: f.for ?? RULE_FORMS })),
      {
        name: "--name",
        value: "name",
        kind: "word",
        about:
          "which of the account's settings — several can share one account, each on its own book · default unless set",
        suggest: ["default"],
        for: ["status", "preview", "on", "now", "off", "log"],
      },
      {
        name: "--share",
        value: "pct",
        kind: "int",
        about:
          "its share of the account's equity, taken at each of its rebalances; the account's running settings add up to 100 at most",
        default: 100,
        min: 1,
        max: 100,
        for: ["backtest", "preview", "on"],
      },
      {
        name: "--with",
        value: "share:flag=value,…",
        kind: "text",
        about:
          "backtest a shared account: one more setting beside this one, on its own book — its share, then its flags without the dashes, like 40:top=3,every=month",
        suggest: ["40:top=3,every=month"],
        for: "backtest",
      },
      {
        name: "--cash",
        value: "dollars",
        kind: "money",
        about: "the backtest's opening cash",
        default: 10000,
        for: ["backtest", "tune"],
      },
      {
        name: "--cost-bps",
        value: "bps",
        kind: "int",
        about: "half the spread, paid on every trade",
        default: 5,
        min: 0,
        max: 100,
        for: ["backtest", "tune"],
      },
      {
        name: "--from",
        value: "YYYY-MM-DD",
        kind: "date",
        about:
          "also simulate your cash from this date, balance week by week; the backtest is unchanged",
        for: "backtest",
      },
      {
        name: "--start",
        value: "YYYY-MM-DD",
        kind: "date",
        about:
          "report only from this date — the same run, cut, so every lookback still has its history",
        for: ["backtest", "tune"],
      },
      {
        name: "--end",
        value: "YYYY-MM-DD",
        kind: "date",
        about: "report only up to this date",
        for: ["backtest", "tune"],
      },
      {
        name: "--split",
        value: "YYYY-MM-DD",
        kind: "date",
        about:
          "choose on the sessions up to this date, judge on the ones after it · the window's middle unless set",
        for: "tune",
      },
      {
        name: "--by",
        value: "cagr|calmar",
        kind: "word",
        about: "what picks the winner: the yearly return, or the yearly return over the worst drop",
        default: "cagr",
        choices: ["cagr", "calmar"],
        for: "tune",
      },
    ],
    examples: [
      {
        line: "rebalance backtest",
        says: "top 5 of the 50 large caps by a year's return over volatility, monthly — the rule the research kept",
      },
      {
        line: "rebalance backtest --every week --top 10 --weight inverse-vol",
        says: "ten names, weekly, more money to the calmer ones",
      },
      {
        line: "rebalance backtest --cash 4000 --invest-pct 95 --from 2025-09-29",
        says: "the six-year backtest, then $4,000 from a year ago, 95% invested, balance each week",
      },
      {
        line: "rebalance backtest --every month --session 2",
        says: "rebalances on each month's second trading session instead of its first",
      },
      {
        line: "rebalance backtest --skip-spike 15 --market spy:200",
        says: "skips a stock that rose over 15% in 5 sessions, and holds cash while SPY is under its 200-session average",
      },
      {
        line: "rebalance preview",
        says: "what the running rebalance on this account would trade if it ran now",
      },
      {
        line: "rebalance preview --top 5 --capital 4000",
        says: "the five it would hold on this account today and the orders it would place, placing none",
      },
      {
        line: "rebalance on --top 5 --every month --capital 4000",
        says: "starts it: the first session of each month, 30 minutes after the open",
      },
      { line: "rebalance off sell", says: "stops it and sells its book" },
      {
        line: "rebalance on --every week --session 5 --top 1 --sell-at 11:00 --buy-at 12:16",
        says: "ranks and sells at 11:00, then buys what it picked at 12:16, New York time; the money is in cash between",
      },
      {
        line: "rebalance on --every week --session 5 --top 1 --at open --buy-at 12:16",
        says: "a bracket: sells at 10:00 and buys at 12:16",
      },
      {
        line: "rebalance on --every week --session 5 --top 1 --at close --sell-at 12:00",
        says: "a bracket: ranks on today's price and sells at 12:00, and buys at the close",
      },
      {
        line: "rebalance on --every week --session 5 --top 1 --sell-at 12:16",
        says: "one minute alone moves the whole rebalance there",
      },
      {
        line: "rebalance on --share 60 --every week --top 1",
        says: "this account's default setting, now on 60% of the account's equity",
      },
      {
        line: "rebalance on --name b --share 40 --every month --top 3",
        says: "a second setting beside it on the same account, on the other 40%, with its own book",
      },
      {
        line: "rebalance backtest --every week --top 1 --lookback 100 --share 60 --with 40:top=3,every=month",
        says: "the two settings on one account, 60/40, each on its own book: the whole account, then each setting's part",
      },
      {
        line: "rebalance preview --name b",
        says: "what setting b would trade now, on its own book",
      },
      {
        line: "rebalance off sell forget --name b",
        says: "stops b, sells its book, and frees its name and its 40%",
      },
      {
        line: "rebalance on --every week --tp-pct 1 --rebuy-under 2 --rebuy-above 3",
        says: "weekly, with a ladder: take 1% and rebuild on a 2% dip or a 3% breakout, every minute",
      },
      {
        line: "rebalance on --every week --session 5 --top 1 --lookback 100 --tp-pct 1.5 --rebuy-under 2.5 --rebuy-above off --ladder-scale vol:40:0.75",
        says: "the ladder scaled to each pick: a calm stock's bands narrow, a volatile one's widen, by its 40-session volatility over the universe's, to the power 0.75",
      },
      {
        line: "rebalance on --every week --session 5 --top 1 --lookback 100 --market spy:50 --market-action hold --tp-pct 1.5 --rebuy-under 2.5 --rebuy-above off --ladder-scale vol:40:0.75 --when dispersion:20>p75:26 --then market=spy:200,tp-pct=1,rebuy-under=2,rebuy-above=4,ladder-scale=off",
        says: "the scaled ladder most weeks; in a week whose returns spread wider than three in four of the last 26, an unscaled 1/2/4 ladder and SPY's 200-session filter instead",
      },
      {
        line: "rebalance preview --when pick-ret:20>8.7 --then tp-pct=2",
        says: "what it would trade now, and whether this week's pick ran more than 8.7% over 20 sessions",
      },
      {
        line: "rebalance compare ladder",
        says: "this account beside the one named ladder, from the later start",
      },
      {
        line: "rebalance backtest --exclude PLTR",
        says: "the default rule without PLTR — on preview and on too, and stored with the schedule",
      },
      {
        line: "rebalance backtest --every week --start 2025-01-01 --end 2025-12-31",
        says: "the same six-year run, reported over 2025 only: every row, both halves and the benchmarks",
      },
      {
        line: "rebalance tune --every week",
        says: "54 weekly settings, each chosen on the first half of the history and judged on the second, with progress as it runs",
      },
      {
        line: "rebalance tune --every week --top 3,5 --lookback 63,126 --split 2024-01-02 --by calmar",
        says: "eight settings chosen on 2024 and before by return over worst drop, judged on what came after",
      },
      { line: "rebalance tune status", says: "follows the latest tune, or prints its report" },
      {
        line: "rebalance backtest --universe allowlist --stop-pct 10",
        says: "only your allowlisted symbols, selling any holding 10% under its entry between rebalances",
      },
    ],
    traps: [
      "`rebalance preview` with no settings previews the account's own rebalance; with ANY setting it previews the defaults plus what you gave, not your current settings changed by one",
      "each setting trades only its own book — the shares its own orders bought. A position bought by hand is left alone, and so is another setting's; it still refuses an account with dca, shares or band running",
      "a share is of the account's equity at each of that setting's rebalances, so one setting's gains are shared with the others at their next rebalance; the running shares add up to 100 at most",
      "a `default` started before settings had books adopts whatever the account holds that no other setting owns, at its ladder's first minute or its first decision",
      "sell shares by hand on a shared account and every book holding them is cut back in proportion",
      "every symbol it may pick must be in symbol_allowlist — leave a name out with --exclude, or `rebalance on` prints the `policy set` line that allows it on every account",
      "--sell-at and --buy-at without --at close rank on yesterday's closes, like --at open: a named minute changes when it trades, not what it ranks on",
      "on an account connected to Alpaca its orders are copied there, and live too when the live mirror is on — a sell larger than live holds is cut to what live holds",
      "a backtest, not a qualification: the default rule was chosen after seeing this history — judge it forward, on paper",
      "prices include dividends (total return), which a paper account does not book — about 1–2% a year",
      "a typed --universe list has no delisted names, so its history flatters it",
      "the day of the month it rebalances moves the result by several points a year — the research saw 26%–35% across start days",
      "the ladder has no backtest: over five years of minute bars no setting beat the plain rule — run it on a second account and `rebalance compare` them",
      "a ladder rebuilds with same-day sale proceeds — copied live, that can be a good-faith violation on a cash account",
      "a tune's winner is the best of many tries on the analysis period — its test result is the honest number, and its test rank says how many settings did better after the split",
      "a window's return is what the account made inside it, holding what the rule held on its first day — not a fresh account opened then",
    ],
    seeAlso: ["strategy", "dca", "schedules"],
  },

  strategy: {
    group: "strategies",
    summary: "whether the evidence supports an M8 investment review",
    forms: [
      { syntax: "strategy [field]", does: "the whole field, ranked" },
      {
        syntax: "strategy qualify <candidate>",
        does: "qualified, rejected or insufficient evidence — and why",
      },
      { syntax: "strategy dossier <candidate>", does: "the evidence package behind a verdict" },
      {
        syntax: "strategy nominate <candidate>",
        does: "open an M8 review for a candidate that qualified",
      },
      { syntax: "strategy holdout", does: "whether the sealed holdout has been opened" },
    ],
    args: [
      {
        name: "candidate",
        kind: "word",
        about: "a candidate id or version — momentum-det or momentum-det@1",
        choices: STRATEGY_CANDIDATES,
      },
    ],
    examples: [
      { line: "strategy", says: "every candidate, ranked against the benchmark" },
      {
        line: "strategy qualify momentum-det",
        says: "the verdict for momentum-det, with its reasons",
      },
    ],
    traps: ["qualifying means the evidence is enough for a review — not that it will make money"],
    seeAlso: ["agent"],
  },

  terms: {
    group: "getting started",
    summary: "the terms of use",
    forms: [
      {
        syntax: "terms",
        does: "the agreement, where it lives on the web, and a chance to accept it if it is outstanding",
      },
    ],
    examples: [{ line: "terms", says: "the terms you agreed to, their version and their address" }],
    seeAlso: ["privacy"],
  },

  history: {
    group: "terminal",
    summary: "the commands you typed, kept in this browser across reloads",
    forms: [
      {
        syntax: "history",
        does: "every command kept, oldest first, numbered — ArrowUp recalls them",
      },
      { syntax: "history clear", does: "forget them all, in this browser" },
    ],
    examples: [
      { line: "history", says: "lists what you typed, the last one at the bottom", local: true },
      { line: "history clear", says: "empties it; a reload stays empty", local: true },
    ],
    traps: [
      "kept in this browser for the signed-in user, up to 500 — another device or browser has its own",
      "a sign-in code or a key you are asked for is never kept",
    ],
    seeAlso: ["clear"],
  },

  theme: {
    group: "terminal",
    summary: "change the colours — any theme works with any setup",
    forms: [{ syntax: "theme [<theme>]", does: "list the themes, or switch to one" }],
    args: [{ name: "theme", kind: "theme", about: "a theme `theme` lists" }],
    examples: [{ line: "theme aubergine", says: "Ubuntu's colours", local: true }],
    seeAlso: ["setup"],
  },

  unwatch: {
    group: "prices",
    summary: "take symbols off the live ticker",
    forms: [{ syntax: "unwatch <symbol>...", does: "stop watching them" }],
    args: [symbol],
    examples: [{ line: "unwatch TSLA", says: "TSLA leaves the ticker", local: true }],
    seeAlso: ["watch"],
  },

  watch: {
    group: "prices",
    summary: "put symbols on the live ticker",
    forms: [
      { syntax: "watch", does: "what the ticker is watching" },
      { syntax: "watch <symbol>...", does: "add them — a symbol with no price is refused" },
    ],
    args: [{ ...symbol, choices: [] }],
    examples: [{ line: "watch NVDA AMD", says: "NVDA and AMD join the ticker" }],
    traps: [
      "every symbol the selected account holds is watched for you, so the header's equity moves with all of it — and leaves the ticker once nothing holds it, unless you watched it yourself",
    ],
    seeAlso: ["unwatch", "quote"],
  },

  whoami: {
    group: "getting started",
    summary: "who you are signed in as",
    forms: [{ syntax: "whoami", does: "your email and user id" }],
    examples: [{ line: "whoami", says: "your email and user id" }],
  },
} as const satisfies Record<string, CommandDoc>;

export type CommandName = keyof typeof CATALOG;
